import type {
  AccessRequestsList,
  ApiError,
  AppsList,
  BillingInterval,
  CheckoutResult,
  InvitePreview,
  InviteInput,
  Me,
  PortalResult,
  ProductsList,
  ReferralInput,
  SeatsView,
  TeamList,
  UpdateSeatsResult,
} from "@opsbelt/contract";

import { API_URL } from "./env";

/** Typed fetch wrapper. Sends the session cookie; throws on non-2xx. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => request<Me>("/api/me"),
  products: () => request<ProductsList>("/api/products"),
  team: () => request<TeamList>("/api/team"),
  invite: (body: InviteInput) =>
    request<{ ok: true; id: string }>("/api/team/invite", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeMember: (id: string) =>
    request<{ ok: true }>(`/api/team/member/${id}`, { method: "DELETE" }),
  renameOrg: (name: string) =>
    request<{ ok: true; name: string }>("/api/team/org", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  invitePreview: (id: string) =>
    request<InvitePreview>(`/api/team/invitations/${id}`),
  billing: {
    checkout: (productId: string, interval: BillingInterval, seats: number) =>
      request<CheckoutResult>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ productId, interval, seats }),
      }),
    updateSeats: (productId: string, seats: number) =>
      request<UpdateSeatsResult>("/api/billing/seats", {
        method: "POST",
        body: JSON.stringify({ productId, seats }),
      }),
    upgrade: () =>
      request<{ ok: true }>("/api/billing/upgrade", { method: "POST" }),
    portal: () =>
      request<PortalResult>("/api/billing/portal", { method: "POST" }),
  },
  onboarding: {
    referral: (body: ReferralInput) =>
      request<{ ok: true }>("/api/onboarding/referral", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  apps: () => request<AppsList>("/api/apps"),
  appSeats: (productId: string) =>
    request<SeatsView>(`/api/apps/${productId}/seats`),
  assignSeat: (productId: string, userId: string) =>
    request<{ ok: true }>("/api/seats", {
      method: "POST",
      body: JSON.stringify({ productId, userId }),
    }),
  unassignSeat: (productId: string, userId: string) =>
    request<{ ok: true }>(`/api/seats/${productId}/${userId}`, {
      method: "DELETE",
    }),
  requestAccess: (productId: string) =>
    request<{ ok: true }>("/api/access-requests", {
      method: "POST",
      body: JSON.stringify({ productId }),
    }),
  accessRequests: () => request<AccessRequestsList>("/api/access-requests"),
  decideRequest: (id: string, decision: "approve" | "deny") =>
    request<{ ok: true }>(`/api/access-requests/${id}/${decision}`, {
      method: "POST",
    }),
};
